import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Button from '../../../shared/components/ui/Button';
import FormInput from '../../../shared/components/ui/FormInput';
import {
  getCompanyKyb,
  addCompanySignatory,
  addCompanyContact
} from '../../services/api/implementation/impl';
import CompanyDocumentPicker from './CompanyDocumentPicker';

/**
 * Setup progress header from the onboarding mockup.
 *
 * The mockup shows three steps (Company Setup → Project Setup → Billing Info). Billing is
 * omitted deliberately: no builder-facing billing model exists in the platform, so the
 * step would be decorative. Add it when there is something behind it.
 */
const SetupProgress = ({ percent }) => {
  const steps = [
    { key: 'company', label: 'Company Setup', active: true, done: percent === 100 },
    { key: 'project', label: 'Project Setup', active: false, done: false }
  ];

  return (
    <div className="rounded-lg border border-stone-200 bg-white p-3">
      <div className="flex items-center justify-between">
        <span className="text-sm text-stone-700">Setup Progress</span>
        <span className="text-sm text-stone-500">{percent}% complete</span>
      </div>
      <div className="mt-2 h-1.5 w-full rounded bg-stone-200">
        <div
          className="h-1.5 rounded bg-emerald-600 transition-all"
          style={{ width: `${percent}%` }}
        />
      </div>
      <div className="mt-3 flex items-center gap-4">
        {steps.map((step) => (
          <span
            key={step.key}
            className={`flex items-center gap-1.5 text-xs ${
              step.active ? 'text-stone-800' : 'text-stone-400'
            }`}>
            <span
              className={`h-2 w-2 rounded-full ${
                step.done ? 'bg-emerald-600' : step.active ? 'bg-emerald-500' : 'bg-stone-300'
              }`}
            />
            {step.label}
          </span>
        ))}
      </div>
    </div>
  );
};

/**
 * Post-create KYB checklist.
 *
 * Signatory, contacts and documents are sub-resources keyed by company_id, so they cannot
 * be collected in the Add Company modal — the company does not exist yet. This panel is
 * where onboarding is finished once it does.
 *
 * The checklist is rendered entirely from GET /company/:id/kyb. The policy lives in the
 * backend's kybPolicyService; duplicating those rules here would guarantee drift.
 */

const STATE_STYLES = {
  satisfied: { dot: 'bg-emerald-500', text: 'text-stone-700' },
  missing: { dot: 'bg-red-500', text: 'text-stone-900' },
  pending_legal_text: { dot: 'bg-stone-300', text: 'text-stone-500' },
  pending_platform: { dot: 'bg-stone-300', text: 'text-stone-500' },
  not_applicable: { dot: 'bg-stone-200', text: 'text-stone-400' }
};

const STATE_LABELS = {
  satisfied: 'Done',
  missing: 'Required',
  pending_legal_text: 'Awaiting legal text',
  pending_platform: 'Not available yet',
  not_applicable: 'Not applicable'
};

const mobileRegex = /^[0-9+\-\s]{6,20}$/;

const signatorySchema = yup.object().shape({
  name: yup.string().required('Name is required').max(255),
  designation: yup.string().required('Designation is required').max(255),
  email: yup.string().required('Work email is required').email('Enter a valid email'),
  mobile: yup
    .string()
    .required('Mobile is required')
    .matches(mobileRegex, 'Enter a valid mobile number'),
  can_accept_terms: yup
    .boolean()
    .oneOf([true], 'Confirm this person can accept terms for the business')
});

const contactSchema = yup.object().shape({
  contact_role: yup.string().required().oneOf(['admin', 'billing', 'technical']),
  name: yup.string().required('Name is required').max(255),
  email: yup.string().required('Email is required').email('Enter a valid email'),
  mobile: yup.string().matches(mobileRegex, {
    message: 'Enter a valid mobile number',
    excludeEmptyString: true
  })
});

/**
 * The authorised signatory declares they can bind the business — `can_accept_terms` is a
 * required checkbox rather than a default, so it is an affirmative act by the operator.
 */
const SignatoryForm = ({ companyId, onSaved }) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting }
  } = useForm({
    resolver: yupResolver(signatorySchema),
    defaultValues: { can_accept_terms: false }
  });

  const onSubmit = async (values) => {
    try {
      await addCompanySignatory(companyId, values);
      reset({ can_accept_terms: false });
      toast.success('Authorised signatory saved');
      if (onSaved) await onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not save the signatory');
    }
  };

  return (
    <div className="flex flex-col gap-3 border-t border-stone-200 pt-4">
      <span className="text-sm">Authorised signatory</span>
      <div className="flex justify-between space-x-4">
        <FormInput
          width={46}
          register={register}
          isRequired
          register_key="name"
          label="Full Name"
          type="text"
          maxLength={255}
          errors={errors.name}
        />
        <FormInput
          width={46}
          register={register}
          isRequired
          register_key="designation"
          label="Designation"
          type="text"
          maxLength={255}
          errors={errors.designation}
        />
      </div>
      <div className="flex justify-between space-x-4">
        <FormInput
          width={46}
          register={register}
          isRequired
          register_key="email"
          label="Work Email"
          type="text"
          errors={errors.email}
        />
        <FormInput
          width={46}
          register={register}
          isRequired
          register_key="mobile"
          label="Mobile"
          type="text"
          maxLength={20}
          errors={errors.mobile}
        />
      </div>
      <label className="flex items-start gap-2 text-xs text-stone-600">
        <input type="checkbox" {...register('can_accept_terms')} />
        This person is authorised to accept terms on behalf of the business.
      </label>
      {errors.can_accept_terms && (
        <span className="text-xs text-red-500">{errors.can_accept_terms.message}</span>
      )}
      <div className="flex justify-end">
        <Button onClick={handleSubmit(onSubmit)} isLoading={isSubmitting} disabled={isSubmitting}>
          Save signatory
        </Button>
      </div>
    </div>
  );
};

const ContactForm = ({ companyId, onSaved }) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting }
  } = useForm({
    resolver: yupResolver(contactSchema),
    defaultValues: { contact_role: 'admin' }
  });

  const onSubmit = async (values) => {
    try {
      await addCompanyContact(companyId, {
        ...values,
        mobile: values.mobile?.trim() || undefined
      });
      reset({ contact_role: 'admin' });
      toast.success('Contact saved');
      if (onSaved) await onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not save the contact');
    }
  };

  return (
    <div className="flex flex-col gap-3 border-t border-stone-200 pt-4">
      <span className="text-sm">Add contact</span>
      <div className="flex justify-between space-x-4">
        <FormInput
          width={46}
          register={register}
          isRequired
          register_key="contact_role"
          label="Role"
          type="dropdown"
          options={[
            { value: 'admin', label: 'Admin' },
            { value: 'billing', label: 'Billing' },
            { value: 'technical', label: 'Technical' }
          ]}
          errors={errors.contact_role}
        />
        <FormInput
          width={46}
          register={register}
          isRequired
          register_key="name"
          label="Full Name"
          type="text"
          maxLength={255}
          errors={errors.name}
        />
      </div>
      <div className="flex justify-between space-x-4">
        <FormInput
          width={46}
          register={register}
          isRequired
          register_key="email"
          label="Email"
          type="text"
          errors={errors.email}
        />
        <FormInput
          width={46}
          register={register}
          register_key="mobile"
          label="Mobile (optional)"
          type="text"
          maxLength={20}
          errors={errors.mobile}
        />
      </div>
      <div className="flex justify-end">
        <Button onClick={handleSubmit(onSubmit)} isLoading={isSubmitting} disabled={isSubmitting}>
          Save contact
        </Button>
      </div>
    </div>
  );
};

const CompanyKybPanel = ({ companyId, onClose }) => {
  const [evaluation, setEvaluation] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      setEvaluation(await getCompanyKyb(companyId));
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not load the onboarding checklist');
    } finally {
      setIsLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    load();
  }, [load]);

  if (isLoading) return <p className="p-4 text-sm text-stone-500">Loading checklist…</p>;
  if (!evaluation) return <p className="p-4 text-sm text-stone-500">No checklist available.</p>;

  const blockingCount = evaluation.requirements.filter(
    (item) => item.blocking && item.state !== 'satisfied'
  ).length;
  const uploadedDocTypes = evaluation.documents || [];

  return (
    <div className="flex flex-col gap-6 p-1">
      <SetupProgress percent={evaluation.percent} />

      <p className="text-xs text-stone-500">
        {blockingCount === 0
          ? 'Nothing outstanding on the customer side.'
          : `${blockingCount} item${blockingCount === 1 ? '' : 's'} still required.`}
      </p>

      <div className="border-t border-stone-200 pt-4">
        <CompanyDocumentPicker
          companyId={companyId}
          entityType={evaluation.company?.entity_type}
          uploadedTypes={uploadedDocTypes}
          onUploaded={load}
        />
      </div>

      <ul className="flex flex-col gap-2">
        {evaluation.requirements.map((item) => {
          const style = STATE_STYLES[item.state] || STATE_STYLES.missing;
          return (
            <li key={item.key} className="flex items-start gap-3">
              <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${style.dot}`} />
              <div className="flex-1">
                <div className="flex items-center justify-between gap-2">
                  <span className={`text-sm ${style.text}`}>{item.label}</span>
                  <span className="text-xs text-stone-400">{STATE_LABELS[item.state]}</span>
                </div>
                {item.detail && <p className="text-xs text-stone-500">{item.detail}</p>}
              </div>
            </li>
          );
        })}
      </ul>

      {evaluation.checks?.some((check) => !check.ok) && (
        <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2">
          {evaluation.checks
            .filter((check) => !check.ok)
            .map((check) => (
              <p key={check.code} className="text-xs text-amber-800">
                {check.message}
              </p>
            ))}
        </div>
      )}

      <SignatoryForm companyId={companyId} onSaved={load} />
      <ContactForm companyId={companyId} onSaved={load} />

      {onClose && (
        <div className="flex justify-end border-t border-stone-200 pt-4">
          <Button displayType="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      )}
    </div>
  );
};

export default CompanyKybPanel;
